import { useMemo } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { useDevice } from "@pushpress/shared-components";
import PaymentComponent from "./PaymentComponent";
import { env } from "../../../env";
import { useGetStripeClientSecret } from "../../../apis/billingApi.hooks";
import { useFetchClientInfo } from "../api/hooks";
import { useAnalytics } from "../../../components/providers/AnalyticsProvider/AnalyticsProvider";
import LoadingScreen from "./LoadingScreen";
import { USER_UUID } from "../constants";
import ErrorState from "./ErrorState";
import ErrorFallback from "./ErrorFallback";
import AddCanadaBank from "./AddCanadaBank";
import { RequestType, stripeAccountType, IsoCurrencyCodes } from "../types";
type AddPaymentProps = {
  clientUuid: string;
  requestType: string;
};

export default function AddPayment({
  clientUuid,
  requestType,
}: AddPaymentProps) {
  const { trackEvent } = useAnalytics();
  const userUuid = sessionStorage.getItem(USER_UUID);
  const { isMobile } = useDevice();
  const {
    data: stripeOptions,
    isLoading: isStripeOptionsLoading,
    error: stripeOptionsError,
    refetch: refetchStripeOptions,
  } = useGetStripeClientSecret(clientUuid, userUuid);

  const { data: clientData, isLoading: isClientDataLoading } =
    useFetchClientInfo();

  // For US gyms or international without international debit enabled
  const platformStripeInfo = useMemo(() => {
    return stripeOptions?.customerDetails.find(
      (customerDetail) =>
        customerDetail.stripeAccountType === stripeAccountType.PLATFORM,
    );
  }, [stripeOptions]);

  const platformStripePromise = useMemo(() => {
    if (platformStripeInfo) {
      return loadStripe(env.STRIPE_PAYMENTS_PUBLISHABLE_KEY);
    }
  }, [platformStripeInfo]);

  // For gyms outside the US that have international debit enabled
  const internationalStripeInfo = useMemo(() => {
    return stripeOptions?.customerDetails.find(
      (customerDetail) =>
        customerDetail.stripeAccountType === stripeAccountType.CONNECT,
    );
  }, [stripeOptions]);

  // For international gyms we need to load stripe with a different publishable key
  const internationalStripePromise = useMemo(() => {
    if (internationalStripeInfo) {
      return loadStripe(internationalStripeInfo.stripePublishableKey);
    }
  }, [internationalStripeInfo]);

  const isCanadaGym =
    clientData?.client?.currencyIso === IsoCurrencyCodes.CANADA;
  const isUSGym = clientData?.client?.currencyIso === IsoCurrencyCodes.USA;

  const isBank = requestType === RequestType.BANK;
  const isCard = requestType === RequestType.CARD;

  const isInternationalBankRequest =
    internationalStripeInfo && internationalStripePromise && !isUSGym && isBank;

  const isCardOrUSGymRequest =
    platformStripeInfo && platformStripePromise && (isUSGym || isCard);

  if (stripeOptionsError) {
    return <ErrorState refetch={refetchStripeOptions} />;
  }

  if (isStripeOptionsLoading || isClientDataLoading) {
    return <LoadingScreen />;
  }

  if (isInternationalBankRequest) {
    trackEvent("Add Payment Stripe Options Viewed", {
      is_mobile: isMobile,
      request_type: requestType,
      client_uuid: clientUuid,
      user_uuid: userUuid,
      is_us_gym: isUSGym,
      is_canada_gym: isCanadaGym,
    });

    if (isCanadaGym) {
      return (
        <Elements
          stripe={internationalStripePromise}
          options={{
            clientSecret: internationalStripeInfo.clientSecret,
          }}
        >
          <AddCanadaBank
            gymName={clientData.client?.company || ""}
            logoUrl={clientData.client?.logoUrl || ""}
            stripeClientSecret={internationalStripeInfo?.clientSecret}
            stripeCustomer={internationalStripeInfo?.stripeCustomer}
          />
        </Elements>
      );
    }

    return (
      <Elements
        stripe={internationalStripePromise}
        options={{
          clientSecret: internationalStripeInfo.clientSecret,
        }}
      >
        <PaymentComponent
          gymName={clientData.client?.company || ""}
          logoUrl={clientData.client?.logoUrl || ""}
          stripeCustomer={internationalStripeInfo.stripeCustomer}
        />
      </Elements>
    );
  }

  if (isCardOrUSGymRequest) {
    trackEvent("Add Payment Stripe Options Viewed", {
      is_mobile: isMobile,
      request_type: requestType,
      client_uuid: clientUuid,
      user_uuid: userUuid,
      is_us_gym: isUSGym,
      is_canada_gym: isCanadaGym,
    });

    return (
      <Elements
        stripe={platformStripePromise}
        options={{
          clientSecret: platformStripeInfo.clientSecret,
        }}
      >
        <PaymentComponent
          gymName={clientData.client?.company || ""}
          logoUrl={clientData.client?.logoUrl || ""}
          stripeCustomer={platformStripeInfo.stripeCustomer}
        />
      </Elements>
    );
  }

  return <ErrorFallback />;
}
